import { motion } from 'framer-motion';
import { Lock, CheckCircle2, Circle, Crown } from 'lucide-react';
import { PremiumBadge } from '@/components/PremiumGate';
import { usePremium } from '@/hooks/usePremium';
import { useAuth } from '@/hooks/useAuth';
import { lessonProgressKey } from '@/lib/deviceProgressState';
import type { LearningPath as LPType } from '@/data/lessons';

interface LearningPathProps {
  paths: LPType[];
  completedLessons: string[];
  totalXp: number;
  onSelectLesson: (lessonId: string) => void;
  assignedLessonIds?: string[];
}

export function LearningPath({ paths, completedLessons, totalXp, onSelectLesson, assignedLessonIds = [] }: LearningPathProps) {
  const { isPremium, setShowUpgrade } = usePremium();
  const { user } = useAuth();


  return (
    <div className="space-y-8">
      {paths.map((path, pi) => {
        const pathFree = isPremium || true; // All paths free for selected sport
        return (
          <div key={path.id}>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-display font-extrabold text-xl">{path.title}</h2>
              {!pathFree && <PremiumBadge />}
            </div>
            <p className="text-sm text-muted-foreground mb-4">{path.description}</p>

            <div className="space-y-4">
              {path.units.map((unit, ui) => {
                const isLocked = totalXp < unit.requiredXp;
                const isPremiumLocked = !pathFree && !isLocked;
                const completedCount = unit.lessons.filter(l => completedLessons.includes(l.id)).length;
                const isComplete = completedCount === unit.lessons.length;

                return (
                  <motion.div
                    key={unit.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (pi * 3 + ui) * 0.1 }}
                    className={`rounded-xl border-2 overflow-hidden transition-all relative ${
                      isLocked || isPremiumLocked
                        ? 'border-border bg-muted/50 opacity-70'
                        : isComplete
                          ? 'border-success/50 bg-success/5'
                          : 'border-primary/30 bg-card shadow-card hover:shadow-elevated'
                    }`}
                  >
                    {isPremiumLocked && (
                      <div
                        onClick={() => setShowUpgrade(true)}
                        className="absolute inset-0 z-10 bg-background/40 backdrop-blur-[1px] rounded-xl flex items-center justify-center cursor-pointer group"
                      >
                        <div className="bg-card/90 rounded-full px-3 py-1.5 flex items-center gap-1.5 border border-border shadow-card group-hover:shadow-elevated transition-shadow">
                          <Crown className="w-3.5 h-3.5 text-accent" />
                          <span className="font-display font-bold text-xs">Unlock with Pro</span>
                        </div>
                      </div>
                    )}

                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-3xl">{unit.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-lg">{unit.title}</h3>
                            {isLocked && <Lock className="w-4 h-4 text-muted-foreground" />}
                            {isComplete && <CheckCircle2 className="w-5 h-5 text-success" />}
                          </div>
                          {isLocked && (
                            <p className="text-xs text-muted-foreground">
                              Requires {unit.requiredXp} XP to unlock
                            </p>
                          )}
                          {!isLocked && (
                            <p className="text-xs text-muted-foreground">
                              {completedCount}/{unit.lessons.length} lessons complete
                            </p>
                          )}
                        </div>
                      </div>

                      {!isLocked && !isPremiumLocked && (
                        <div className="space-y-1.5">
                          {unit.lessons.map(lesson => {
                            const done = completedLessons.includes(lesson.id);
                            const isAssigned = assignedLessonIds.includes(lesson.id);
                            const hasPartialProgress = (() => {
                              try {
                                const raw = localStorage.getItem(lessonProgressKey(user?.id, lesson.id));
                                return raw ? JSON.parse(raw)?.lessonId === lesson.id : false;
                              } catch { return false; }

                            })();
                            return (
                              <button
                                key={lesson.id}
                                onClick={() => onSelectLesson(lesson.id)}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                                  done
                                    ? 'bg-success/10 hover:bg-success/15'
                                    : isAssigned
                                      ? 'bg-primary/10 hover:bg-primary/15 border border-primary/30 ring-1 ring-primary/10'
                                      : hasPartialProgress
                                        ? 'bg-accent/10 hover:bg-accent/15 border border-accent/20'
                                        : 'bg-muted/50 hover:bg-muted'
                                }`}
                              >
                                {done ? (
                                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                                ) : hasPartialProgress ? (
                                  <div className="w-5 h-5 rounded-full border-2 border-accent flex-shrink-0 flex items-center justify-center">
                                    <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                                  </div>
                                ) : (
                                  <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="font-body font-semibold text-sm truncate">{lesson.title}</p>
                                    {isAssigned && !done && (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[9px] font-display font-bold flex-shrink-0">
                                        📋 Assigned
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {hasPartialProgress && !done ? '▶ Resume' : lesson.description}
                                  </p>
                                </div>
                                <span className="text-xs font-display font-bold text-accent flex-shrink-0">
                                  +{lesson.xpReward} XP
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
